import React from 'react';
import styled from 'styled-components';
import {AccentColorOptions} from 'epic-ue-theme';
import {Grid} from '@material-ui/core';
import {DownloadOptions, DownloadOptionsProps} from './DownloadSteps.options';
import {
    EmbeddedVideo,
    EmbeddedVideoProps,
    GithubCard,
    GithubCardProps,
    GridContainer,
    TextHeading,
    TextHeadingProps,
    Typography,
    VerticalSpacer
} from '../../elements';
import {AnimateOnViewWrap} from '../../wraps/animation/AnimateOnViewWrap';
import {NumberedElipse} from '../../elements/icons/NumberedElipse';
import {getBowserState} from '@epic-core/common';
import {ContextMessage} from '@epic-mw/localization';
import {Spotlight} from '../../wraps/spotlight/Spotlight.wrap';
import {isValidMediaOptions} from '../../../utils';

const Styled = styled.div<{backgroundColorInitial?: boolean}>`
    position: relative;

    background-color: ${(p) =>
        p.backgroundColorInitial
            ? 'initial'
            : p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};

    .ue-text-image-card-img img {
        max-height: 500px;
    }

    .image-card .mui-paper::before {
        background: #000;
    }

    .ue-spacer.custom {
        width: 100%;
    }
    .ue-text-heading > .ue-grid-container {
        ${(p) => p.theme.breakpoints.down('sm')} {
            padding: 0;
        }
    }
    ${(p) => p.theme.breakpoints.down('sm')} {
        .embedded-video > .ue-grid-container {
            padding: 0;
        }
    }
    ${(p) => p.theme.breakpoints.down('xs')} {
        .ue-numbered-elipse {
            position: relative;
            left: -4px;
        }
    }
`;

interface TextHeadingPropsWithStep extends TextHeadingProps {
    step: number;
    downloadOptions: DownloadOptionsProps;
    embedVideo: EmbeddedVideoProps;
    githubCard?: GithubCardProps;
}

export interface DownloadStepsProps {
    accentColor?: AccentColorOptions;
    embedVideoSpotlightColor?: AccentColorOptions;
    steps?: TextHeadingPropsWithStep[];
    backgroundColorInitial?: boolean;
    downloadCards: React.ReactNode[];
}

export const DownloadSteps = (props: DownloadStepsProps): JSX.Element | null => {
    const {
        steps = [],
        backgroundColorInitial,
        downloadCards,
        embedVideoSpotlightColor = 'galaxy'
    } = props;

    const {mobile, tablet} = getBowserState();

    return (
        <Styled className="download-steps" backgroundColorInitial={backgroundColorInitial}>
            <div className="hero-text-wrapper">
                <VerticalSpacer variant="custom" spacing={16} downSmSpacing={10} />
                <GridContainer>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="stretch">
                        {steps.length ? (
                            <>
                                {steps.map((stepsProps, index) => {
                                    return (
                                        <div key={`download-step-${index}`}>
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="flex-start"
                                                alignItems="stretch">
                                                <Grid item md={1}>
                                                    &nbsp;
                                                </Grid>
                                                <Grid item sm={12} md={1}>
                                                    <NumberedElipse text={stepsProps?.step} />
                                                </Grid>
                                                <Grid item sm={12} md={10}>
                                                    <TextHeading
                                                        {...stepsProps}
                                                        key={`download-step-heading-${index}`}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="flex-start"
                                                alignItems="stretch">
                                                {isValidMediaOptions(
                                                    stepsProps?.embedVideo?.mediaOptions
                                                ) ? (
                                                    <>
                                                        <Grid item md={1} lg={2}>
                                                            &nbsp;
                                                        </Grid>
                                                        <Grid item xs={12} md={10} lg={9}>
                                                            <VerticalSpacer
                                                                variant="custom"
                                                                spacing={4}
                                                            />
                                                            <AnimateOnViewWrap
                                                                delay={0.15}
                                                                translateY={30}>
                                                                <Spotlight
                                                                    accentColor={
                                                                        embedVideoSpotlightColor
                                                                    }
                                                                    variant="big"
                                                                    shape="oval"
                                                                    tilt="left"
                                                                    offsetRatio={[0.1, -0.2]}
                                                                    optMaxWidth={1600}>
                                                                    <EmbeddedVideo
                                                                        noGridSides
                                                                        {...stepsProps.embedVideo}
                                                                    />
                                                                </Spotlight>
                                                            </AnimateOnViewWrap>
                                                        </Grid>
                                                    </>
                                                ) : (
                                                    <>
                                                        {stepsProps?.downloadOptions &&
                                                            stepsProps?.downloadOptions?.cards &&
                                                            stepsProps?.downloadOptions?.cards[0]
                                                                ?.title && (
                                                                <>
                                                                    <Grid item md={1} lg={2}>
                                                                        &nbsp;
                                                                    </Grid>
                                                                    <Grid item xs={12} md={10}>
                                                                        {(mobile || tablet) && (
                                                                            <>
                                                                                <VerticalSpacer variant="heading" />
                                                                                <GridContainer>
                                                                                    <Typography
                                                                                        variant="h4"
                                                                                        big>
                                                                                        <ContextMessage code="epic.launcher.download.mobile.tips" />
                                                                                    </Typography>
                                                                                </GridContainer>
                                                                            </>
                                                                        )}
                                                                        <VerticalSpacer
                                                                            variant="custom"
                                                                            downSmSpacing={6}
                                                                            spacing={10}
                                                                        />
                                                                        <DownloadOptions
                                                                            {...stepsProps?.downloadOptions}
                                                                            downloadCards={
                                                                                downloadCards
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                </>
                                                            )}
                                                    </>
                                                )}
                                            </Grid>
                                            <VerticalSpacer variant="section" />

                                            {stepsProps?.githubCard?.githubLink && (
                                                <>
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="flex-start"
                                                        alignItems="stretch">
                                                        <Grid item lg={1}>
                                                            &nbsp;
                                                        </Grid>
                                                        <Grid item sm={12} lg={10}>
                                                            <GridContainer>
                                                                <AnimateOnViewWrap
                                                                    delay={0.15}
                                                                    translateY={30}>
                                                                    <GithubCard
                                                                        {...stepsProps?.githubCard}
                                                                    />
                                                                </AnimateOnViewWrap>
                                                            </GridContainer>
                                                        </Grid>
                                                        <Grid item lg={1} />
                                                    </Grid>
                                                    <VerticalSpacer variant="section" />
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        ) : (
                            ''
                        )}
                    </Grid>
                </GridContainer>
            </div>
        </Styled>
    );
};
